/**
 * ТЗ 0120, этап 2 — «Договоры» как CRM: фильтры в адресе, быстрые чипы,
 * поповер «Фильтры (N)» и сохранённые виды. Чистые помощники без React,
 * Apollo и i18n (их проверяют тесты). Общая часть — `_shared/savedViewsModel`.
 *
 * Ключи фильтра совпадают с белым списком сервера для видов `contracts`
 * (`CONTRACT_VIEW_FILTER_KEYS`): вид хранит ровно то, что видно в адресе.
 * В адресе имена те же, кроме группы: `?group=<id>` — параметр окна «Новый
 * договор», поэтому фильтр группы живёт в `?inGroup=`.
 */

import {
  filtersToViewJson,
  sameFiltersBy,
  viewJsonRecord,
  viewJsonString as asString,
  viewJsonStrings as asStrings,
} from '../_shared/savedViewsModel';
import {
  APLORA_GOOD_WIN_CHANCE,
  APLORA_HIGH_WIN_CHANCE,
} from '../Crm/aploraModel';
import { ContractsView, withContractsView } from './contractsBoardModel';

export interface IContractsFilters {
  /** Поиск (номер, имя, email, телефон, PESEL, NIP, № лида). */
  q: string;
  /** «Только мои» — я продавец (у HR — я создал). */
  mine: boolean;
  status: string[];
  kind: string;
  /** Зачисление: pending | failed | done. */
  enrolment: string;
  seller: string;
  sentBy: string;
  group: string;
  createdFrom: string;
  createdTo: string;
  /** `high` — шанс лида ≥ 70 % (только роли лидов). */
  chance: string;
  /** Состояние подписи (любое из). */
  signState: string[];
  /** Живая ссылка истекает не позже чем через N дней. */
  expiring: string;
  /** «Нужно действие». */
  needsAction: boolean;
  /** created (по умолчанию — пусто) | expires | win_chance | activity. */
  sort: string;
}

export const EMPTY_CONTRACTS_FILTERS: IContractsFilters = {
  q: '',
  mine: false,
  status: [],
  kind: '',
  enrolment: '',
  seller: '',
  sentBy: '',
  group: '',
  createdFrom: '',
  createdTo: '',
  chance: '',
  signState: [],
  expiring: '',
  needsAction: false,
  sort: '',
};

type FilterKey = keyof IContractsFilters;

/** Параметр адреса для каждого фильтра. */
export const CONTRACTS_FILTER_PARAMS: Readonly<Record<FilterKey, string>> = {
  q: 'q',
  mine: 'mine',
  status: 'status',
  kind: 'kind',
  enrolment: 'enrolment',
  seller: 'seller',
  sentBy: 'sentBy',
  group: 'inGroup',
  createdFrom: 'createdFrom',
  createdTo: 'createdTo',
  chance: 'chance',
  signState: 'signState',
  expiring: 'expiring',
  needsAction: 'needsAction',
  sort: 'sort',
};

const FILTER_KEYS = Object.keys(CONTRACTS_FILTER_PARAMS) as FilterKey[];

/** Состояния подписи (как `signState` сервера). */
export const CONTRACT_SIGN_STATES = [
  'not_sent',
  'not_opened',
  'clicked',
  'opened',
  'signed',
  'closed',
] as const;
export type ContractSignState = typeof CONTRACT_SIGN_STATES[number];

/** Сортировки; `created` — по умолчанию (в адресе пусто). */
export const CONTRACT_SORTS = [
  'created',
  'expires',
  'win_chance',
  'activity',
] as const;
export type ContractSort = typeof CONTRACT_SORTS[number];

/**
 * «Живые» договоры — сделка ещё не решена: только у них подсветка высокого
 * шанса (лента, зелёная карточка, «горячих», чип). Подписанный или
 * отклонённый / истёкший / отменённый договор горячим не бывает.
 */
export const CONTRACT_LIVE_STATUSES = [
  'draft',
  'sending',
  'send_failed',
  'sent',
  'opened',
] as const;

export const isContractLive = (status: string | null | undefined): boolean =>
  (CONTRACT_LIVE_STATUSES as readonly string[]).includes(status || '');

/** «Высокий шанс» — тот же порог, что у лидов. */
export const CONTRACT_CHANCE_HIGH = 'high';
/** ТЗ 0127: «хороший и выше» (≥ 55 %) — тот же порог, что у лидов. */
export const CONTRACT_CHANCE_GOOD = 'good';

const isChance = (value: string): boolean =>
  value === CONTRACT_CHANCE_HIGH || value === CONTRACT_CHANCE_GOOD;

/** Порог шанса фильтра; нет фильтра — `null`. */
export const contractChanceMin = (chance: string): number | null => {
  if (chance === CONTRACT_CHANCE_HIGH) return APLORA_HIGH_WIN_CHANCE;
  if (chance === CONTRACT_CHANCE_GOOD) return APLORA_GOOD_WIN_CHANCE;
  return null;
};
/** Статус, которого нет: «под фильтры не попало ничего». */
export const CONTRACT_NO_STATUS = '__none__';
/** Чип «Истекает ≤ 3 дн.». */
export const CONTRACT_EXPIRING_SOON = '3';
/** Варианты «Истекает» в поповере, дней. */
export const CONTRACT_EXPIRING_OPTIONS = ['1', '3', '7'] as const;
/** Значение «Я» в выборе продавца (= «Только мои»). */
export const CONTRACT_SELLER_ME = '__me';

const asDays = (value: string): string =>
  /^\d{1,2}$/.test(value) && Number(value) <= 60 ? String(Number(value)) : '';

const pickKnown = <T extends string>(
  values: ReadonlyArray<string>,
  allowed: ReadonlyArray<T>,
): T[] =>
  Array.from(new Set(values.filter((v): v is T => allowed.includes(v as T))));

/** Фильтры из адреса; мусор отбрасывается. */
export const parseContractsFilters = (search: string): IContractsFilters => {
  const params = new URLSearchParams(search);
  const get = (key: FilterKey) =>
    (params.get(CONTRACTS_FILTER_PARAMS[key]) || '').trim();
  const list = (key: FilterKey) =>
    get(key)
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  const sort = get('sort');
  return {
    q: get('q'),
    mine: get('mine') === '1',
    status: Array.from(new Set(list('status'))),
    kind: get('kind'),
    enrolment: get('enrolment'),
    seller: get('seller'),
    sentBy: get('sentBy'),
    group: get('group'),
    createdFrom: get('createdFrom'),
    createdTo: get('createdTo'),
    chance: isChance(get('chance')) ? get('chance') : '',
    signState: pickKnown(list('signState'), CONTRACT_SIGN_STATES),
    expiring: asDays(get('expiring')),
    needsAction: get('needsAction') === '1',
    sort:
      sort !== 'created' && (CONTRACT_SORTS as readonly string[]).includes(sort)
        ? sort
        : '',
  };
};

const paramValue = (value: IContractsFilters[FilterKey]): string | null => {
  if (Array.isArray(value)) return value.length ? value.join(',') : null;
  if (typeof value === 'boolean') return value ? '1' : null;
  return value || null;
};

/**
 * Адрес с изменёнными фильтрами: остальные параметры (окно договора, вид,
 * вкладка) не трогаются.
 */
export const serializeContractsFilters = (
  search: string,
  patch: Partial<IContractsFilters>,
): string => {
  const params = new URLSearchParams(search);
  (Object.keys(patch) as FilterKey[]).forEach((key) => {
    if (!(key in CONTRACTS_FILTER_PARAMS)) return;
    const value = paramValue(patch[key] as IContractsFilters[FilterKey]);
    const name = CONTRACTS_FILTER_PARAMS[key];
    if (value) params.set(name, value);
    else params.delete(name);
  });
  return params.toString();
};

/**
 * Фильтры для роли: без ролей лидов «Шанс» и сортировка по шансу не
 * существуют (их нет в интерфейсе и сервер их не применяет); без договоров
 * студентов (HR) — продавец, отправитель и группа (запрос их не
 * применяет — крошка и сброс показывали бы фильтр, которого нет).
 */
export const contractsFiltersForRole = (
  filters: IContractsFilters,
  leadRoles: boolean,
  canReadStudent = true,
): IContractsFilters => {
  if (leadRoles && canReadStudent) return filters;
  let next = filters;
  if (!leadRoles) {
    next = {
      ...next,
      chance: '',
      sort: next.sort === 'win_chance' ? '' : next.sort,
    };
  }
  if (!canReadStudent) {
    next = { ...next, seller: '', sentBy: '', group: '' };
  }
  return next;
};

/** Задан ли хоть один фильтр (сортировка — не фильтр). */
export const hasContractsFilters = (filters: IContractsFilters): boolean =>
  FILTER_KEYS.some(
    (key) => key !== 'sort' && paramValue(filters[key]) !== null,
  );

/** Есть что сбрасывать: фильтр или сортировка не по умолчанию. */
export const canResetContractsFilters = (filters: IContractsFilters): boolean =>
  hasContractsFilters(filters) || Boolean(filters.sort);

/* ── Переменные запроса ─────────────────────────────────────────────── */

export interface IContractsAccess {
  /** id текущего сотрудника. */
  meId: string | null;
  /** Договоры студентов и фирм (продавец, отправитель, группа). */
  canReadStudent: boolean;
  /** Роли лидов: шанс Aplora и сортировка по нему. */
  leadRoles: boolean;
}

/** Переменные списка договоров (без страницы). */
export interface IContractsQueryFilters {
  search: string | null;
  status: string | null;
  kind: string | null;
  fulfilmentStatus: string | null;
  groupId: string | null;
  createdById: string | null;
  sellerId: string | null;
  sentById: string | null;
  dateFrom: string | null;
  dateTo: string | null;
  aploraMinWinChance: number | null;
  signState: string[] | null;
  expiringWithinDays: number | null;
  needsAction: boolean | null;
  sort: string | null;
}

/**
 * Статусы, которые реально спрашиваются: при «Шанс ≥ 70 %» (у ролей лидов)
 * — только живые (пересечение с выбранными; ничего не выбрано — все живые).
 * Так чип фильтрует ровно то, что считает.
 */
export const contractsEffectiveStatuses = (
  filters: Pick<IContractsFilters, 'status' | 'chance'>,
  leadRoles: boolean,
): string[] => {
  if (!leadRoles || !isChance(filters.chance)) {
    return filters.status;
  }
  const live = CONTRACT_LIVE_STATUSES as readonly string[];
  if (!filters.status.length) return [...live];
  return filters.status.filter((status) => live.includes(status));
};

/**
 * Фильтры → переменные запроса. «Только мои» = «я продавец» (ТЗ 0078); у
 * роли без договоров студентов (HR) — «я создал». Шанс и сортировка по нему
 * — только ролям лидов (остальным сервер их не применяет, а интерфейс их не
 * показывает).
 */
export const contractsQueryFilters = (
  filters: IContractsFilters,
  access: IContractsAccess,
): IContractsQueryFilters => {
  const mine = filters.mine && access.meId ? access.meId : null;
  const { canReadStudent, leadRoles } = access;
  const sort =
    filters.sort && (filters.sort !== 'win_chance' || leadRoles)
      ? filters.sort
      : null;
  const chanceOn = leadRoles && isChance(filters.chance);
  const statuses = contractsEffectiveStatuses(filters, leadRoles);
  // Выбраны только решённые статусы при «Шансе»: ни одного живого — пусто
  // (несуществующий статус), а не «все».
  const status =
    chanceOn && !statuses.length
      ? CONTRACT_NO_STATUS
      : statuses.join(',') || null;
  return {
    search: filters.q || null,
    status,
    kind: filters.kind || null,
    fulfilmentStatus: filters.enrolment || null,
    groupId: canReadStudent ? filters.group || null : null,
    createdById: canReadStudent ? null : mine,
    sellerId: canReadStudent ? mine || filters.seller || null : null,
    sentById: canReadStudent ? filters.sentBy || null : null,
    dateFrom: filters.createdFrom || null,
    dateTo: filters.createdTo || null,
    aploraMinWinChance: leadRoles ? contractChanceMin(filters.chance) : null,
    signState: filters.signState.length ? filters.signState : null,
    expiringWithinDays: filters.expiring ? Number(filters.expiring) : null,
    needsAction: filters.needsAction || null,
    sort,
  };
};

/* ── Быстрые чипы ───────────────────────────────────────────────────── */

export const CONTRACT_QUICK_FILTERS = [
  'needsAction',
  'opened',
  'expiring',
  'hot',
  // ТЗ 0127: «Хороший+» — шанс ≥ 55 % (высокие тоже).
  'good',
] as const;
export type ContractQuickFilter = typeof CONTRACT_QUICK_FILTERS[number];

const onlyOpened = (states: ReadonlyArray<string>) =>
  states.length === 1 && states[0] === 'opened';

export const contractQuickPressed = (
  filters: IContractsFilters,
  chip: ContractQuickFilter,
): boolean => {
  switch (chip) {
    case 'needsAction':
      return filters.needsAction;
    case 'opened':
      return onlyOpened(filters.signState);
    case 'expiring':
      return filters.expiring === CONTRACT_EXPIRING_SOON;
    case 'good':
      return filters.chance === CONTRACT_CHANCE_GOOD;
    case 'hot':
    default:
      return filters.chance === CONTRACT_CHANCE_HIGH;
  }
};

/** Фильтр, который ставит чип (для счётчика на чипе — и для нажатия). */
export const contractQuickPatch = (
  chip: ContractQuickFilter,
): Partial<IContractsFilters> => {
  switch (chip) {
    case 'needsAction':
      return { needsAction: true };
    case 'opened':
      return { signState: ['opened'] };
    case 'expiring':
      return { expiring: CONTRACT_EXPIRING_SOON };
    case 'good':
      return { chance: CONTRACT_CHANCE_GOOD };
    case 'hot':
    default:
      return { chance: CONTRACT_CHANCE_HIGH };
  }
};

/** Нажатие чипа: второе нажатие снимает. */
export const toggleContractQuick = (
  filters: IContractsFilters,
  chip: ContractQuickFilter,
): Partial<IContractsFilters> => {
  if (!contractQuickPressed(filters, chip)) return contractQuickPatch(chip);
  const key = Object.keys(contractQuickPatch(chip))[0] as FilterKey;
  return { [key]: EMPTY_CONTRACTS_FILTERS[key] } as Partial<IContractsFilters>;
};

/* ── Поповер «Фильтры (N)» и крошки ─────────────────────────────────── */

/** Фильтры поповера; `created` — пара «создан с / по». */
export const CONTRACT_HIDDEN_FILTERS = [
  'status',
  'kind',
  'enrolment',
  'seller',
  'sentBy',
  'group',
  'created',
  'signState',
  'expiring',
  'sort',
] as const;
export type ContractHiddenFilter = typeof CONTRACT_HIDDEN_FILTERS[number];

/**
 * Активен ли фильтр поповера. Статусы — только на доске (в «Списке» они
 * видны чипами); состояние подписи и срок — когда их не показывает нажатый
 * быстрый чип.
 */
export const contractHiddenActive = (
  filters: IContractsFilters,
  key: ContractHiddenFilter,
  view: ContractsView,
): boolean => {
  switch (key) {
    case 'status':
      return view === 'board' && filters.status.length > 0;
    case 'created':
      return Boolean(filters.createdFrom || filters.createdTo);
    case 'signState':
      return filters.signState.length > 0 && !onlyOpened(filters.signState);
    case 'expiring':
      return (
        Boolean(filters.expiring) && filters.expiring !== CONTRACT_EXPIRING_SOON
      );
    default:
      return Boolean(filters[key]);
  }
};

export const contractHiddenFilters = (
  filters: IContractsFilters,
  view: ContractsView,
): ContractHiddenFilter[] =>
  CONTRACT_HIDDEN_FILTERS.filter((key) =>
    contractHiddenActive(filters, key, view),
  );

/** Снять один фильтр поповера (крошка ✕). */
export const clearContractHidden = (
  key: ContractHiddenFilter,
): Partial<IContractsFilters> => {
  if (key === 'created') return { createdFrom: '', createdTo: '' };
  return {
    [key]: EMPTY_CONTRACTS_FILTERS[key],
  } as Partial<IContractsFilters>;
};

/* ── Сохранённые виды ───────────────────────────────────────────────── */

/** Ключи фильтра, которые хранит вид (белый список сервера). */
export const CONTRACT_VIEW_FILTER_KEYS = FILTER_KEYS;

/** Фильтры → JSON вида: только непустые ключи. */
export const contractsFiltersToView = (filters: IContractsFilters) =>
  filtersToViewJson(CONTRACT_VIEW_FILTER_KEYS, filters);

/**
 * JSON вида (чужая запись, старая версия) → фильтры: каждое значение
 * проходит тот же разбор, что адрес, — мусор отбрасывается.
 */
export const contractsFiltersFromView = (
  raw: unknown,
  leadRoles = true,
  canReadStudent = true,
): IContractsFilters => {
  const record = viewJsonRecord(raw);
  const draft: IContractsFilters = {
    q: asString(record.q),
    mine: record.mine === true,
    status: asStrings(record.status),
    kind: asString(record.kind),
    enrolment: asString(record.enrolment),
    seller: asString(record.seller),
    sentBy: asString(record.sentBy),
    group: asString(record.group),
    createdFrom: asString(record.createdFrom),
    createdTo: asString(record.createdTo),
    chance: asString(record.chance),
    signState: asStrings(record.signState),
    expiring: asString(record.expiring),
    needsAction: record.needsAction === true,
    sort: asString(record.sort),
  };
  // Общий вид от роли лидов («горячие») у финансиста — без «Шанса».
  return contractsFiltersForRole(
    parseContractsFilters(serializeContractsFilters('', draft)),
    leadRoles,
    canReadStudent,
  );
};

/** Те же фильтры (порядок значений в списке не в счёт). */
export const sameContractsFilters = (
  a: IContractsFilters,
  b: IContractsFilters,
): boolean => sameFiltersBy(CONTRACT_VIEW_FILTER_KEYS, a, b);

/**
 * Адрес после выбора вида: фильтры вида целиком заменяют текущие, вид
 * доски/списка — из записи; окна (`?contract=` и т. п.) и вкладка — на месте.
 */
export const applyContractsView = (
  search: string,
  filters: IContractsFilters,
  view: ContractsView,
): string =>
  withContractsView(
    serializeContractsFilters(search, {
      ...EMPTY_CONTRACTS_FILTERS,
      ...filters,
    }),
    view,
  );

/** Готовые виды — у всех, без сервера; «горячие» — только ролям лидов. */
export const CONTRACT_VIEW_PRESETS = [
  'myAction',
  'hotUnsigned',
  'expiringWeek',
] as const;
export type ContractViewPreset = typeof CONTRACT_VIEW_PRESETS[number];

export const contractViewPresets = (leadRoles: boolean): ContractViewPreset[] =>
  CONTRACT_VIEW_PRESETS.filter(
    (preset) => leadRoles || preset !== 'hotUnsigned',
  );

export const contractViewPresetFilters = (
  preset: ContractViewPreset,
): IContractsFilters => {
  switch (preset) {
    case 'myAction':
      return { ...EMPTY_CONTRACTS_FILTERS, mine: true, needsAction: true };
    case 'hotUnsigned':
      return {
        ...EMPTY_CONTRACTS_FILTERS,
        chance: CONTRACT_CHANCE_HIGH,
        signState: ['not_opened', 'clicked', 'opened'],
        sort: 'win_chance',
      };
    case 'expiringWeek':
    default:
      return { ...EMPTY_CONTRACTS_FILTERS, expiring: '7', sort: 'expires' };
  }
};
